import { useEffect, useMemo, useRef, useState } from "react";

import { useLocalization } from "@fluent/react";
import {
  Button,
  Checkbox,
  Chip,
  LoadingShim,
  Popover,
  Row,
  TextInput,
} from "@narmi/design_system";

import styles from "./TransactionTypeFilter.module.scss";

import type { BaiTransactionType } from "../../hooks/useBaiTransactionTypes";

interface TransactionTypeFilterProps {
  /** Currently selected BAI2 type codes. Empty array = all types. */
  selectedCodes: string[];
  onChange: (codes: string[]) => void;
  types: BaiTransactionType[];
  isLoading: boolean;
  error: string | null;
}

/** Ordered categories to render */
const CATEGORIES = ["credit", "debit", "misc"] as const;

type TransactionCategory = (typeof CATEGORIES)[number];

/** FTL IDs for category section headers */
const CATEGORY_FTL_IDS: Record<TransactionCategory, string> = {
  credit: "transaction-export-type-filter-category-credit",
  debit: "transaction-export-type-filter-category-debit",
  misc: "transaction-export-type-filter-category-misc",
};

/** Format the label for a transaction type, handling null descriptions. */
const formatTypeLabel = (type: BaiTransactionType): string =>
  type.description ? `${type.code} — ${type.description}` : type.code;

/** An empty selection means "all types", so seed the draft with every code. */
const buildInitialDraft = (
  selectedCodes: string[],
  types: BaiTransactionType[]
): Set<string> =>
  selectedCodes.length === 0
    ? new Set(types.map((t) => t.code))
    : new Set(selectedCodes);

const TransactionTypeFilter = ({
  selectedCodes,
  onChange,
  types,
  isLoading,
  error,
}: TransactionTypeFilterProps) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Local draft state — only committed to parent on Apply
  const [draft, setDraft] = useState<Set<string>>(() =>
    buildInitialDraft(selectedCodes, types)
  );

  // Opening the popover mid-load seeds the draft from an empty `types`, which
  // renders every box unchecked. Reseed once types arrive while still open so
  // the all-selected default holds.
  const hadTypes = useRef(types.length > 0);
  useEffect(() => {
    if (isOpen && !hadTypes.current && types.length > 0) {
      setDraft(buildInitialDraft(selectedCodes, types));
    }
    hadTypes.current = types.length > 0;
  }, [isOpen, types, selectedCodes]);

  const filteredTypes = useMemo(() => {
    if (!searchQuery.trim()) return types;
    const query = searchQuery.trim().toLowerCase();
    return types.filter(
      (type) =>
        type.code.toLowerCase().includes(query) ||
        (type.description?.toLowerCase().includes(query) ?? false)
    );
  }, [searchQuery, types]);

  const handleToggleCode = (code: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    const allFilteredSelected = filteredTypes.every((t) => draft.has(t.code));
    setDraft((prev) => {
      const next = new Set(prev);
      filteredTypes.forEach((t) => {
        if (allFilteredSelected) {
          next.delete(t.code);
        } else {
          next.add(t.code);
        }
      });
      return next;
    });
  };

  const handleApply = () => {
    // Selecting everything is the unfiltered default, committed as an empty list.
    const allSelected =
      types.length > 0 && types.every((t) => draft.has(t.code));
    onChange(allSelected ? [] : [...draft]);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleRemoveFilter = () => {
    onChange([]);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleOpen = () => {
    setDraft(buildInitialDraft(selectedCodes, types));
    setSearchQuery("");
    setIsOpen(true);
  };

  const handleDismiss = () => {
    setIsOpen(false);
  };

  const chipLabel = l10n.getString("transaction-export-type-filter-label");
  // An empty selection is the "all types" default, so it reads as all selected.
  const allSelected =
    selectedCodes.length === 0 ||
    (types.length > 0 && selectedCodes.length === types.length);

  const renderCategory = (category: TransactionCategory) => {
    const typesInCategory = filteredTypes.filter(
      (t) => t.direction === category
    );
    if (typesInCategory.length === 0) return null;

    return (
      <div key={category}>
        <span
          className={`fontSize--xs fontWeight--bold margin--bottom--s nds-typescale--upper ${styles["type-filter-popover-category-header"]}`}
        >
          {l10n.getString(CATEGORY_FTL_IDS[category])}
        </span>
        {typesInCategory.map((type: BaiTransactionType) => (
          <div key={type.code} className="margin--bottom--m">
            <Checkbox
              label={formatTypeLabel(type)}
              checked={draft.has(type.code)}
              onChange={() => handleToggleCode(type.code)}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderPopoverBody = () => {
    if (isLoading) {
      return (
        <div
          className={styles["type-filter-popover-state"]}
          data-testid="type-filter-loading"
        >
          <LoadingShim isLoading>
            <div className={styles["type-filter-popover-state-placeholder"]} />
          </LoadingShim>
        </div>
      );
    }

    if (error) {
      return (
        <div
          className={styles["type-filter-popover-state"]}
          data-testid="type-filter-error"
        >
          <span className="fontSize--s fontColor--error">{error}</span>
        </div>
      );
    }

    if (types.length === 0) {
      return (
        <div
          className={styles["type-filter-popover-state"]}
          data-testid="type-filter-empty"
        >
          <span className="fontSize--s fontColor--secondary">
            {l10n.getString("transaction-export-type-filter-empty")}
          </span>
        </div>
      );
    }

    return (
      <>
        <div className={styles["type-filter-popover-search"]}>
          <TextInput
            placeholder={l10n.getString(
              "transaction-export-type-filter-search"
            )}
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            testId="type-filter-search"
          />
        </div>
        <div className={styles["type-filter-popover-divider"]} />
        {filteredTypes.length > 0 && (
          <>
            <div className={styles["type-filter-popover-select-all"]}>
              <Checkbox
                label={l10n.getString(
                  "transaction-export-type-filter-select-all"
                )}
                checked={filteredTypes.every((t) => draft.has(t.code))}
                onChange={handleToggleSelectAll}
                testId="type-filter-select-all"
              />
            </div>
            <div className={styles["type-filter-popover-divider"]} />
          </>
        )}
        <div className={styles["type-filter-popover-list"]}>
          {CATEGORIES.map(renderCategory)}
        </div>
        <div className={styles["type-filter-popover-divider"]} />
        <div className="padding--x--s padding--y--xs">
          <Row justifyContent="space-between" alignItems="center">
            <Button
              kind="plain"
              size="xs"
              label={l10n.getString("transaction-export-type-filter-remove")}
              onClick={handleRemoveFilter}
              type="button"
              testId="type-filter-remove"
            />
            <Button
              kind="primary"
              size="xs"
              label={l10n.getString("transaction-export-type-filter-apply")}
              onClick={handleApply}
              testId="type-filter-apply"
            />
          </Row>
        </div>
      </>
    );
  };

  const popoverContent = (
    <div className={styles["type-filter-popover"]}>{renderPopoverBody()}</div>
  );

  return (
    <Popover
      content={popoverContent}
      alignment="start"
      isOpen={isOpen}
      onUserDismiss={handleDismiss}
      testId="type-filter-popover"
    >
      <Chip
        kind="secondary"
        label={chipLabel}
        startIcon="filter"
        count={
          allSelected
            ? l10n.getString("transaction-export-filter-count-all")
            : String(selectedCodes.length)
        }
        onClick={handleOpen}
        endIcon={`chevron-${isOpen ? "up" : "down"}`}
      />
    </Popover>
  );
};

export default TransactionTypeFilter;
